import { Storyblok } from '../../storyblok';
import { isEmpty } from 'lodash';

import { BreakpointName, media } from '../media';

export const parseRenderSettings = (
    renderSettings?: Storyblok.Component.RenderSettings,
) => {
    if (!renderSettings || isEmpty(renderSettings)) return {};

    let storyblokCustomStyles: string[] = [];
    let storyblokCustomProps: { [key: string]: string } = {};

    renderSettings.find((renderSetting) => {
        /**
         * Allows to controll module's custom styles via Storyblok.
         * To achieve this in Storyblok, you need to add a component named
         * 'customStyles` in module's settings component.
         * 
         * There are 4 possible breakpoint rules:
         * - all
         * - min_{BreakpointName},
         * - max_{BreakpointName},
         * - between_{BreakpointName}_{BreakpointName}
         *
         * 
         * @example `min_lg` set in Storyblok will be parsed as:
         * @media only screen and (min-width: 1024px) {
                body {
                    background-color: cornflowerblue;
                }
            }
         */
        if (renderSetting.component === 'customStyles') {
            const styles = renderSetting.items.map(({ rule, breakpoints }) => {
                /**
                 * By splitting this string with `_` we can easly create rules
                 * based on {BreakpointName} from Storyblok.
                 */
                const breakpointRule = breakpoints.split('_');

                /**
                 * Returns rule without any media queries
                 */
                if (breakpointRule.includes('all')) return rule;
                /**
                 * Returns rule wrapped with @media `min` or `max`
                 * Possible breakpoints: `sm, md, lg, xl`
                 * @see https://eduardoboucas.github.io/include-media/
                 */
                if (
                    breakpointRule.includes('min') ||
                    breakpointRule.includes('max')
                )
                    return `${media[breakpointRule[0] as 'min' | 'max'](
                        breakpointRule[1] as BreakpointName,
                    )} { ${rule} }`;
                /**
                 * Returns rule wrapped with @media `between` two breakpoints.
                 * Possible breakpoints: `sm, md, lg, xl`
                 * @see https://eduardoboucas.github.io/include-media/
                 */
                if (breakpointRule.includes('between'))
                    return `${media.between(
                        breakpointRule[1] as BreakpointName,
                        breakpointRule[2] as BreakpointName,
                    )} { ${rule} }`;

                return '';
            });

            storyblokCustomStyles = [...storyblokCustomStyles, ...styles];
        }

        /**
         * Allows to controll module's backgroundImage via Storyblok.
         * To achieve this in Storyblok, you need to add a component named
         * 'backgroundImage` in module's settings component.
         */
        if (renderSetting.component === 'backgroundImage') {
            storyblokCustomStyles = [
                ...storyblokCustomStyles,
                `
                    background-image: url(${renderSetting.image.filename});
                    ${renderSetting.styles}
            `,
            ];
        }

        /**
         * Allows to controll module's props (HTML attributes) via Storyblok.
         * To achieve this in Storyblok, you need to add a component named
         * 'props` in module's settings component.
         */
        if (renderSetting.component === 'props') {
            storyblokCustomProps = renderSetting.items.reduce(
                (obj, item) => ({
                    ...obj,
                    [item.name]:
                        item.value === 'true' || item.value === 'false'
                            ? item.value === 'true'
                            : item.value,
                }),
                {},
            );
        }
    });

    return { storyblokCustomStyles, storyblokCustomProps };
};
